import React from 'react';
import styled from 'styled-components';
import type { FacilityPhase, FacilityPhaseCode } from '../types/facilityWorkspace';

/**
 * Work plan item F: the facility phase, once, in the facility header. RAMP_UP (delayed draw, before
 * the first draw), REVOLVING (to the commitment termination date), AMORTIZATION (after it, to
 * maturity), TERMED_OUT (after an approved term-out), MATURED. Derived server-side from the version
 * in force and the tenant business date; this shows the code, its dates and the one-line basis.
 */

const LABEL: Record<FacilityPhaseCode, string> = {
  RAMP_UP: 'Ramp-up',
  REVOLVING: 'Revolving',
  AMORTIZATION: 'Amortising',
  TERMED_OUT: 'Termed out',
  MATURED: 'Matured',
};

const TONE: Record<FacilityPhaseCode, { fg: string; bg: string; border: string }> = {
  RAMP_UP: { fg: '#bae6fd', bg: 'rgba(14,116,144,.22)', border: 'rgba(125,211,252,.45)' },
  REVOLVING: { fg: '#bbf7d0', bg: 'rgba(22,101,52,.25)', border: 'rgba(134,239,172,.45)' },
  AMORTIZATION: { fg: '#fde68a', bg: 'rgba(146,64,14,.25)', border: 'rgba(252,211,77,.45)' },
  TERMED_OUT: { fg: '#fecaca', bg: 'rgba(153,27,27,.25)', border: 'rgba(252,165,165,.45)' },
  MATURED: { fg: '#cbd5e1', bg: 'rgba(51,65,85,.35)', border: 'rgba(148,163,184,.4)' },
};

const Wrap = styled.div`
  display: inline-flex; align-items: center; gap: 8px; margin-top: 6px; flex-wrap: wrap;
  font-size: 12px; color: #94a3b8; line-height: 1.4;
`;

const Chip = styled.span<{ $fg: string; $bg: string; $border: string }>`
  display: inline-flex; align-items: center; gap: 6px; padding: 2px 9px; border-radius: 999px;
  border: 1px solid ${p => p.$border}; background: ${p => p.$bg}; color: ${p => p.$fg};
  font-size: 11px; font-weight: 650; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap;
`;

const fmt = (value: string | null | undefined): string => {
  if (!value) return '';
  const d = new Date(value + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const phaseLabel = (code: FacilityPhaseCode | string): string => LABEL[code as FacilityPhaseCode] || String(code).replace(/_/g, ' ');

/** The dates the phase runs between, in words: "since 2 Oct 2026 · until 2 Oct 2028". */
export const phaseDates = (phase: FacilityPhase): string => {
  const parts: string[] = [];
  if (phase.since) parts.push(`since ${fmt(phase.since)}`);
  if (phase.until) parts.push(`until ${fmt(phase.until)}`);
  return parts.join(' · ');
};

const FacilityPhaseBadge: React.FC<{ phase?: FacilityPhase | null }> = ({ phase }) => {
  if (!phase || !phase.code) return null;
  const tone = TONE[phase.code] || TONE.MATURED;
  const dates = phaseDates(phase);
  return (
    <Wrap data-testid="facility-phase" title={phase.basis} aria-label={`Facility phase ${phaseLabel(phase.code)}`}>
      <Chip $fg={tone.fg} $bg={tone.bg} $border={tone.border}>{phaseLabel(phase.code)}</Chip>
      {dates && <span>{dates}</span>}
      {phase.basis && <span style={{ color: '#7f93ad' }}>· {phase.basis}</span>}
    </Wrap>
  );
};

export default FacilityPhaseBadge;
